import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { AppState } from '../app.state';
import { UserService } from '../../services/user.service';
import * as AvailableOffersActions from '../available-offers/available-offers.actions';
import { unformatCnpj } from '../../utils/cnpj.util';

import * as UserActions from './user.actions';
import { selectLoginData, selectUserUuid } from './user.selectors';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<AppState>);
  private readonly userService = inject(UserService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly SELECTED_CNPJ_KEY = 'umkt_selected_cnpj';

  private cleanCnpj(cnpj: string): string {
    return unformatCnpj(cnpj);
  }

  private isSavedValueCnpj(value: string): boolean {
    return /^[A-Z0-9]{14}$/.test(this.cleanCnpj(value));
  }

  loadUserDetails$ = createEffect(() => 
    this.actions$.pipe(
      ofType(UserActions.loadUserDetails),
      switchMap(({ uuid }) =>
        this.userService.getUserByUuid(uuid).pipe(
          map(response => {
            if (isPlatformBrowser(this.platformId)) {
              const saved = localStorage.getItem(this.SELECTED_CNPJ_KEY);
              if (saved && response.data && this.isSavedValueCnpj(saved)) {
                response.data.document = saved;
              }
            }
            return UserActions.loadUserDetailsSuccess({ user: response });
          }),
          catchError(error => of(UserActions.loadUserDetailsFailure({ 
            error: error.message || 'Erro ao carregar detalhes do usuário' 
          })))
        )
      )
    )
  );

  loadAvailableOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.selectCnpj),
      withLatestFrom(this.store.select(selectLoginData)),
      filter(([_, loginData]) => {
        return !!(loginData?.resources && loginData.resources.length > 0);
      }),
      switchMap(([{ document }]) => [
        AvailableOffersActions.loadAvailableOffers({ cnpj: document })
      ])
    )
  );
  
  selectCnpj$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.selectCnpj),
      withLatestFrom(this.store.select(selectUserUuid)),
      switchMap(([{ document }, userUuid]) => {

        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem(this.SELECTED_CNPJ_KEY, document);
        }

        if (!userUuid) {
          console.warn('⚠️ UserUuid não encontrado, não foi possível atualizar CNPJ no backend');
          return of(null);
        }

        const cleanDocument = this.cleanCnpj(document);
        
        return this.userService.updateUserDocument(userUuid, cleanDocument).pipe(
          tap((response: any) => {
            console.log('CNPJ atualizado com sucesso:', response);
          }),
          catchError(error => {
            console.error('Erro ao atualizar CNPJ:', error);
            // Não falha - mantém mudança local mesmo se backend falhar
            return of(null);
          })
        );
      })
    ),
    { dispatch: false }
  );

  logoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logoutSuccess),
      tap((_: any) => {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.removeItem(this.SELECTED_CNPJ_KEY);
        }
      })
    ),
    { dispatch: false }
  );
}
